import type {
  ChatFile,
  Invite,
  InviteCode,
  LogEvent,
  Media,
  MeResponse,
  OnlineSubResult,
  TrashItem,
  OutboxMessage,
  Overview,
  PublicUser,
  RoomRecord,
  ServerConfig,
  SettingsResponse,
  Subtitle,
  SystemCheck,
  TeamView,
  User,
  Volume,
  SessionReport,
  NetCapture,
  HealthLive,
} from './types';

declare global {
  interface Window {
    __VB__?: { apiUrl?: string };
  }
}

/**
 * Where the server lives. Empty = same origin (the app served by the Mac mini itself).
 * On Vercel, /config.js (written by `videobridge export-web`) or VITE_API_URL points here.
 */
export const API_BASE = (window.__VB__?.apiUrl || import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

export const apiUrl = (path: string) => API_BASE + path;

// Signing in: the server keeps a 30-day refresh key in an HttpOnly cookie that scripts can't read
// (only POST /api/auth/… gets it), and this page holds just a 1-hour access token in memory,
// renewed every 20 minutes and when the page comes back. A script injected into the page can't
// carry off a token that outlives it. Tokens from before this (kept in web storage) are traded in
// once for the cookie, then deleted.
const LEGACY_KEY = 'vb.token';
const RENEW_MS = 20 * 60_000;
let token: string | null = null;
let tokenAt = 0;
let renewTimer: ReturnType<typeof setTimeout> | undefined;
let renewing: Promise<boolean> | null = null;

export const getToken = (): string | null => token;

export function setToken(t: string | null) {
  token = t;
  tokenAt = Date.now();
  clearTimeout(renewTimer);
  if (t) renewTimer = setTimeout(() => void renewToken(), RENEW_MS);
  else dropLegacyToken();
}

function legacyToken(): string | null {
  try {
    return sessionStorage.getItem(LEGACY_KEY) ?? localStorage.getItem(LEGACY_KEY);
  } catch {
    return null;
  }
}

function dropLegacyToken() {
  try {
    sessionStorage.removeItem(LEGACY_KEY);
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* storage unavailable (private mode) */
  }
}

/** Trades the refresh cookie for a fresh access token. False if nobody is signed in here. */
export function renewToken(): Promise<boolean> {
  renewing ??= (async () => {
    const legacy = legacyToken();
    try {
      const res = await fetch(apiUrl('/api/auth/token'), {
        method: 'POST',
        credentials: API_BASE ? 'include' : 'same-origin',
        headers: legacy ? { Authorization: `Bearer ${legacy}` } : undefined,
      });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) setToken(null);
        return false;
      }
      setToken(((await res.json()) as Session).token);
      // The old token goes only after the cookie had time to reach disk (Android's WebView writes
      // cookies every ~30 s; an app closed sooner would otherwise come back signed out).
      if (legacy) setTimeout(dropLegacyToken, 120_000);
      return true;
    } catch {
      return !!token; // offline: keep what we have
    } finally {
      renewing = null;
    }
  })();
  return renewing;
}

// A phone that slept for an hour wakes with an expired token: renew before anything else runs.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && token && Date.now() - tokenAt > RENEW_MS) void renewToken();
  });
}

/** Requests that sign in (set the refresh cookie) or need it. */
const usesCookie = (path: string) => path.startsWith('/api/auth/') || path === '/api/me/password';

let mediaToken = '';
export const setMediaToken = (t: string) => (mediaToken = t);

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown, init?: RequestInit, retried = false): Promise<T> {
  const headers: Record<string, string> = {};
  const sent = getToken();
  if (sent) headers.Authorization = `Bearer ${sent}`;
  const isBlob = body instanceof Blob;
  if (body !== undefined && !isBlob) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method,
      headers,
      body: body === undefined ? undefined : isBlob ? body : JSON.stringify(body),
      credentials: !API_BASE ? 'same-origin' : usesCookie(path) ? 'include' : 'omit',
      ...init,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, "Can't reach the Video Bridge server — is the host's Mac online?");
  }
  if (!res.ok) {
    if (res.status === 401 && sent && !usesCookie(path)) {
      // The access token ran out (a laptop that slept): renew once and try again.
      if (!retried && (await renewToken())) return request<T>(method, path, body, init, true);
      window.dispatchEvent(new Event('vb:unauthorized'));
    }
    throw await responseError(res);
  }
  return (await readBody(res)) as T;
}

/** JSON when the body parses as JSON, else the raw text; an empty body is {}. */
async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return text;
  }
}

/** The ApiError for a failed raw fetch, carrying the server's `error` message like request() does. */
export async function responseError(res: Response): Promise<ApiError> {
  const data = await readBody(res).catch(() => ({}));
  return new ApiError(res.status, (data as { error?: string })?.error ?? res.statusText, data);
}

type Session = { token: string; user: User };
type PublicKeyCredentialRequestOptionsJSON = import('@simplewebauthn/browser').PublicKeyCredentialRequestOptionsJSON;
type PublicKeyCredentialCreationOptionsJSON = import('@simplewebauthn/browser').PublicKeyCredentialCreationOptionsJSON;

export const api = {
  config: () => request<ServerConfig>('GET', '/api/config'),

  // account
  signup: (b: { name: string; email: string; password: string; city: string; role: 'host' | 'member'; code?: string }) => request<Session>('POST', '/api/auth/signup', b),
  login: (email: string, password: string) => request<Session>('POST', '/api/auth/login', { email, password }),
  passkeyLoginBegin: () => request<{ ceremony: string; options: PublicKeyCredentialRequestOptionsJSON }>('POST', '/api/auth/passkey/begin'),
  passkeyLoginFinish: (ceremony: string, answer: unknown) => request<Session>('POST', `/api/auth/passkey/finish?ceremony=${encodeURIComponent(ceremony)}`, answer),
  passkeys: () => request<{ id: string; name: string; created: string; lastUsed?: string; kind?: 'passkey' | 'phone' }[]>('GET', '/api/passkeys'),
  addPhoneKey: (name: string) => request<{ user: string; id: string; secret: string }>('POST', '/api/devicekeys', { name }),
  phoneKeyLogin: (key: { user: string; id: string; secret: string }) => request<Session>('POST', '/api/auth/devicekey', key),
  passkeyRegisterBegin: () => request<{ ceremony: string; options: PublicKeyCredentialCreationOptionsJSON }>('POST', '/api/passkeys/register/begin'),
  passkeyRegisterFinish: (ceremony: string, name: string, answer: unknown) =>
    request<{ id: string; name: string }>('POST', `/api/passkeys/register/finish?ceremony=${encodeURIComponent(ceremony)}&name=${encodeURIComponent(name)}`, answer),
  deletePasskey: (id: string) => request('DELETE', `/api/passkeys/${encodeURIComponent(id)}`),
  logout: () => request('POST', '/api/auth/logout'),
  verify: (token: string) => request<{ ok: boolean; user: User; joinedTeam: boolean }>('POST', '/api/auth/verify', { token }),
  forgot: (email: string) => request<{ ok: boolean; emailEnabled: boolean }>('POST', '/api/auth/forgot', { email }),
  reset: (token: string, password: string) => request<Session>('POST', '/api/auth/reset', { token, password }),
  signin: (token: string) => request<Session>('POST', '/api/auth/signin', { token }),
  me: () => request<MeResponse>('GET', '/api/me'),
  updateProfile: (b: Partial<Pick<User, 'name' | 'city' | 'bio' | 'theme' | 'lang' | 'prefs'>>) => request<User>('PATCH', '/api/me', b),
  uploadAvatar: (file: Blob) => request<User>('POST', '/api/me/avatar', file),
  deleteAvatar: () => request<User>('DELETE', '/api/me/avatar'),
  changePassword: (current: string, next: string) => request<Session>('POST', '/api/me/password', { current, new: next }),
  resendVerification: () => request<{ ok: boolean; emailEnabled: boolean }>('POST', '/api/me/verify/resend'),
  mySessions: () => request<unknown[] | null>('GET', '/api/me/sessions'),
  signinLink: (ttlHours?: number) =>
    request<{ url: string; expiresAt: number; reusable: boolean }>('POST', '/api/me/signin-links', ttlHours ? { ttlHours } : undefined),

  // team
  team: () => request<TeamView>('GET', '/api/team'),
  updateTeam: (b: { name?: string; defaults?: TeamView['defaults'] }) => request('PATCH', '/api/team', b),
  updateMember: (id: string, perms: TeamView['defaults']) => request('PATCH', `/api/team/members/${id}`, { perms }),
  removeMember: (id: string) => request('DELETE', `/api/team/members/${id}`),
  searchUsers: (q: string) => request<PublicUser[] | null>('GET', `/api/users/search?q=${encodeURIComponent(q)}`),
  pendingUsers: () => request<PublicUser[] | null>('GET', '/api/users/pending'),
  verifyUser: (id: string) => request<PublicUser>('POST', `/api/users/${id}/verify`),
  makeHost: (id: string) => request('POST', `/api/users/${id}/make-host`),
  declineHost: (id: string) => request('POST', `/api/users/${id}/decline-host`),
  invites: () => request<Invite[] | null>('GET', '/api/invites'),
  createInvite: (b: { kind: 'user' | 'email' | 'link'; userId?: string; email?: string; ttlHours?: number }) =>
    request<{ invite: Invite; url?: string; emailEnabled: boolean }>('POST', '/api/invites', b),
  revokeInvite: (id: string) => request('DELETE', `/api/invites/${id}`),
  myInvites: () => request<Invite[] | null>('GET', '/api/invites/mine'),
  acceptInvite: (id: string) => request('POST', `/api/invites/${id}/accept`),
  declineInvite: (id: string) => request('POST', `/api/invites/${id}/decline`),
  previewLink: (token: string) =>
    request<{ teamName: string; kind: string; email?: string; byName?: string; byCity?: string }>('GET', `/api/invites/link/${encodeURIComponent(token)}`),
  joinByLink: (token: string) => request('POST', `/api/invites/link/${encodeURIComponent(token)}/join`),
  inviteCodes: () => request<InviteCode[]>('GET', '/api/invite-codes'),
  createInviteCode: (b: { ttlHours: number; maxUses: number }) => request<InviteCode>('POST', '/api/invite-codes', b),
  revokeInviteCode: (id: string) => request('DELETE', `/api/invite-codes/${id}`),
  previewInviteCode: (code: string) =>
    request<{ teamName: string; code: string; byName?: string; byCity?: string; expiresAt: string }>('GET', `/api/invite-codes/${encodeURIComponent(code)}`),
  joinWithCode: (code: string) => request('POST', `/api/invite-codes/${encodeURIComponent(code)}/join`),
  outbox: () => request<{ configured: boolean; messages: OutboxMessage[] | null }>('GET', '/api/mail/outbox'),
  testMail: () => request('POST', '/api/mail/test'),

  // library
  overview: () => request<Overview>('GET', '/api/overview'),
  netCaptures: () => request<{ active?: NetCapture; saved: NetCapture[] | null; names: Record<string, string> }>('GET', '/api/netcapture'),
  startCapture: (label: string) => request<NetCapture>('POST', '/api/netcapture/start', { label }),
  stopCapture: () => request<NetCapture>('POST', '/api/netcapture/stop'),
  deleteCapture: (id: string) => request('DELETE', `/api/netcapture/${id}`),
  healthLive: (after: number, signal?: AbortSignal) => request<HealthLive>('GET', `/api/health/live?after=${after}`, undefined, { signal }),
  media: () => request<Media[]>('GET', '/api/media'),
  mediaItem: (id: string) => request<Media>('GET', `/api/media/${id}`),
  renameMedia: (id: string, title: string) => request<Media>('PATCH', `/api/media/${id}`, { title }),
  deleteMedia: (id: string) => request('DELETE', `/api/media/${id}`),
  reprocess: (id: string) => request<Media>('POST', `/api/media/${id}/reprocess`),
  encodePlan: (id: string, plan: { qualities: number[]; audio: string[]; start: boolean }) => request<Media>('POST', `/api/media/${id}/encode`, plan),
  stopEncode: (id: string) => request<Media>('POST', `/api/media/${id}/encode/stop`),
  trash: () => request<{ items: TrashItem[] | null; bytes: number }>('GET', '/api/trash'),
  restoreTrash: (kind: TrashItem['kind'], id: string) => request('POST', `/api/trash/${kind}/${id}/restore`),
  purgeTrash: (kind: TrashItem['kind'], id: string) => request('DELETE', `/api/trash/${kind}/${id}`),
  emptyTrash: () => request<{ deleted: number }>('DELETE', '/api/trash'),
  removeOriginal: (id: string) => request<{ media: Media; removedNow: boolean }>('POST', `/api/media/${id}/remove-original`),
  requestFullRes: (id: string) => request<{ queued: boolean }>('POST', `/api/media/${id}/full-res`),
  jobLog: async (id: string) => {
    const res = await fetch(apiUrl(`/api/media/${id}/joblog`), { headers: { Authorization: `Bearer ${getToken()}` } });
    // An error page must not be shown as the processing log.
    if (!res.ok) throw await responseError(res);
    return res.text();
  },
  createUpload: (fileName: string, size: number, title?: string) =>
    request<{ media: Media; chunkSize: number }>('POST', '/api/uploads', { fileName, size, title }),
  uploadStatus: (id: string) => request<{ offset: number; size: number; status: string }>('GET', `/api/uploads/${id}`),
  uploadSubtitle: (id: string, file: File, lang: string, label?: string) =>
    request<{ media: Media; subtitle: Subtitle; charset: string }>(
      'POST',
      `/api/media/${id}/subtitles?${new URLSearchParams({ name: file.name, lang, label: label ?? '' })}`,
      file,
    ),
  searchSubtitles: (id: string, lang: string) => request<{ results: OnlineSubResult[] | null }>('GET', `/api/media/${id}/subtitles/search?lang=${encodeURIComponent(lang)}`),
  addOnlineSubtitle: (id: string, r: OnlineSubResult) => request<{ media: Media; subtitle: Subtitle }>('POST', `/api/media/${id}/subtitles/online`, r),
  updateSubtitle: (id: string, sid: string, b: { lang?: string; label?: string }) => request<Media>('PATCH', `/api/media/${id}/subtitles/${sid}`, b),
  deleteSubtitle: (id: string, sid: string) => request<Media>('DELETE', `/api/media/${id}/subtitles/${sid}`),
  uploadChunk: (id: string, offset: number, chunk: Blob, signal?: AbortSignal, base?: string) =>
    base ? localChunk(base, id, offset, chunk, signal) : request<{ offset: number; status: string }>('PUT', `/api/uploads/${id}?offset=${offset}`, chunk, { signal }),

  // rooms
  rooms: () => request<RoomRecord[]>('GET', '/api/rooms'),
  room: (id: string) => request<RoomRecord>('GET', `/api/rooms/${id}`),
  createRoom: (name: string, mediaId?: string) => request<RoomRecord>('POST', '/api/rooms', { name, mediaId }),
  roomReport: (id: string, session?: number) => request<SessionReport>('GET', `/api/rooms/${id}/report${session ? `?session=${session}` : ''}`),
  updateRoomCover: (id: string, mediaId: string) => request<RoomRecord>('PATCH', `/api/rooms/${id}/cover`, { mediaId }),
  inviteToRoom: (id: string, userIds: string[]) => request<{ invited: string[] }>('POST', `/api/rooms/${id}/invite`, { userIds }),
  pushKey: () => request<{ key: string }>('GET', '/api/push/key'),
  pushSubscribe: (sub: PushSubscriptionJSON, device: string) => request('POST', '/api/push/subscribe', { sub, device }),
  pushSubscribeApp: (fcm: string, device: string) => request('POST', '/api/push/subscribe', { fcm, device }),
  pushUnsubscribe: (endpoint: string) => request('POST', '/api/push/unsubscribe', { endpoint }),
  dismissRoomInvite: (id: string) => request('POST', `/api/room-invites/${id}/dismiss`),
  uploadRoomCover: (id: string, file: Blob) => request<RoomRecord>('POST', `/api/rooms/${id}/cover-image`, file),
  watchTogether: (mediaId: string) => request<{ room: RoomRecord; joined: boolean }>('POST', '/api/rooms/watch', { mediaId }),
  deleteRoom: (id: string) => request('DELETE', `/api/rooms/${id}`),
  livekitToken: (roomId: string) => request<{ url: string; token: string; identity: string }>('POST', `/api/rooms/${roomId}/livekit`),

  // host
  settings: () => request<SettingsResponse>('GET', '/api/settings'),
  updateSettings: (b: Record<string, unknown>) => request<{ needsRestart: boolean }>('PATCH', '/api/settings', b),
  setService: (background: boolean) => request<{ handoff: boolean }>('POST', '/api/settings/service', { background }),
  restart: () => request('POST', '/api/settings/restart'),
  checkPublic: () =>
    request<{ lanIp: string; publicIp?: string; host: string; port: number; resolves?: string[]; dnsMatches?: boolean }>('GET', '/api/settings/check'),
  volumes: () => request<Volume[]>('GET', '/api/volumes'),
  logs: (q: { level?: string; cat?: string; q?: string; limit?: number }) =>
    request<{ events: LogEvent[] | null; names: Record<string, string> }>(
      'GET',
      `/api/logs?${new URLSearchParams(Object.entries(q).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]))}`,
    ),
  logFiles: () => request<{ files: string[] | null; dir: string }>('GET', '/api/logs/files'),

  // first-run setup (on the Mac itself)
  setupStatus: () =>
    request<{ checks: SystemCheck[]; volumes: Volume[]; lanIp: string; hostname: string; configPath: string; service?: { supported: boolean } }>('GET', '/api/setup/status'),
  checkLibrary: (path: string) => request('POST', '/api/setup/check-library', { path }),
  openPrivacy: () => request('POST', '/api/setup/open-privacy'),
  completeSetup: (b: Record<string, unknown>) => request<Session & { handoff: boolean }>('POST', '/api/setup/complete', b),

  // speed test
  speedResult: (b: { downMbps: number; upMbps: number; pingMs: number; jitterMs: number; verdict: string }) =>
    request('POST', '/api/speed/result', b),
  apps: () => request<{ android?: { version: string; versionCode: number; size: number; file: string; sha256: string; updatedAt: string; minAndroid: string } }>('GET', '/api/apps'),
};

/** URL for <img>/<video> elements, which can't send an Authorization header. */
export function mediaUrl(path: string) {
  const sep = path.includes('?') ? '&' : '?';
  return apiUrl(path) + (mediaToken ? `${sep}mt=${encodeURIComponent(mediaToken)}` : '');
}

export const thumbUrl = (m: Pick<Media, 'id' | 'hasThumb' | 'updatedAt'>) =>
  m.hasThumb ? mediaUrl(`/media/${m.id}/thumb.jpg?v=${encodeURIComponent(m.updatedAt)}`) : undefined;

export const avatarUrl = (u: { id?: string; userId?: string; avatar?: string }) => {
  const id = u.userId ?? u.id;
  return u.avatar && id ? mediaUrl(`/api/users/${id}/avatar?v=${u.avatar}`) : undefined;
};

export const roomCoverUrl = (r: { id: string; coverImage?: string }) => (r.coverImage ? mediaUrl(`/api/rooms/${r.id}/cover-image?v=${r.coverImage}`) : undefined);

export const hlsUrl = (id: string) => apiUrl(`/media/${id}/hls/master.m3u8`);

export const subtitleUrl = (mediaId: string, sid: string, v?: string) =>
  apiUrl(`/media/${mediaId}/subs/${sid}.vtt${v ? `?v=${encodeURIComponent(v)}` : ''}`);

// ---- local upload lane ------------------------------------------------------------------------
// A browser running on the host Mac itself would otherwise send every byte out over the home
// internet connection and back in through the public address (~15–25 Mbit/s). The server's
// always-on local listener takes the bytes directly (gigabits per second). It's used only when
// it answers as the very same server instance.
let lane: Promise<string | null> | null = null;

export function uploadLane(): Promise<string | null> {
  lane ??= (async () => {
    try {
      const cfg = await api.config();
      const local = (cfg as { localUrl?: string }).localUrl;
      const instance = (cfg as { instance?: string }).instance;
      if (!local || !instance || apiUrl('') === local || location.origin === local) return null;
      const r = await fetch(`${local}/api/health`, { signal: AbortSignal.timeout(1500), cache: 'no-store' });
      if (!r.ok) return null;
      const j = (await r.json()) as { instance?: string };
      return j.instance === instance ? local : null;
    } catch {
      return null;
    }
  })();
  return lane;
}

async function localChunk(base: string, id: string, offset: number, chunk: Blob, signal?: AbortSignal) {
  const res = await fetch(`${base}/api/uploads/${id}?offset=${offset}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: chunk,
    signal,
  });
  if (!res.ok) throw await responseError(res);
  return (await readBody(res)) as { offset: number; status: string };
}

/** Largest file the chat takes (the server refuses more). */
export const CHAT_FILE_MAX = 50 * 1024 * 1024;

/**
 * Send a file to a room's chat. XHR rather than fetch, for upload progress. Resolves with the
 * file's details for the chat message; rejects with the server's reason.
 */
export function uploadChatFile(roomId: string, file: File, onProgress: (fraction: number) => void, signal: AbortSignal, retried = false): Promise<ChatFile> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', apiUrl(`/api/rooms/${roomId}/files?name=${encodeURIComponent(file.name)}`));
    const tok = getToken();
    if (tok) xhr.setRequestHeader('Authorization', `Bearer ${tok}`);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      if (xhr.status === 401 && !retried) {
        renewToken().then((ok) => (ok ? uploadChatFile(roomId, file, onProgress, signal, true).then(resolve, reject) : reject(new ApiError(401, 'please sign in'))));
        return;
      }
      let body: { error?: string } & Partial<ChatFile> = {};
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        /* not JSON */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as ChatFile);
      else reject(new ApiError(xhr.status, body.error || `upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new ApiError(0, "Can't reach the Video Bridge server"));
    xhr.onabort = () => reject(new DOMException('cancelled', 'AbortError'));
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

export const chatFileUrl = (roomId: string, f: { id: string }, download = false) => mediaUrl(`/api/rooms/${roomId}/files/${f.id}${download ? '?download=1' : ''}`);
